import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, TrendingUp, Rocket, MessageCircle, CalendarDays, Radio, CalendarClock, MessageSquare, Users, MoreHorizontal, Sparkles, PlusCircle, Landmark, UserCog, ExternalLink, Compass, X, Target, ClipboardList, CalendarRange, Settings, Gift, Gauge } from "lucide-react";

const STAFF_ROLES = ["super_admin", "admin", "credit_specialist", "team_member"];
const ADMIN_ROLES = ["super_admin", "admin"];

export default function MobileBottomNav({ role }) {
  const loc = useLocation();
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isStaff = STAFF_ROLES.includes(role);
  const isTradelineAdmin = role === "tradeline_admin";
  if (role !== "client" && !isStaff && !isTradelineAdmin) return null;

  const items = role === "client" ? [
    { to: "/portal",           label: "Home",     icon: LayoutDashboard, match: /^\/portal\/?$/, scrollTo: "top" },
    { to: "/portal",           label: "Progress", icon: TrendingUp,      match: /^\/portal\/?$/, scrollTo: "tracker" },
    { to: "/portal/messages",  label: "Messages", icon: MessageCircle,   match: /^\/portal\/messages/ },
    { to: "/portal/meetings",  label: "Meetings", icon: CalendarDays,    match: /^\/portal\/meetings/ },
    { moreMenu: true,          label: "More",     icon: MoreHorizontal },
  ] : isTradelineAdmin ? [
    { to: "/tradeline-admin", label: "Ops",     icon: Landmark,        match: /^\/tradeline-admin/ },
    { to: "/profile",         label: "Profile", icon: UserCog,         match: /^\/profile/ },
    { moreMenu: true,         label: "More",    icon: MoreHorizontal },
  ] : [
    { to: "/admin",              label: "Clients",  icon: LayoutDashboard, match: /^\/admin\/?$/ },
    { to: "/admin/work-queue",   label: "Queue",    icon: ClipboardList,   match: /^\/admin\/work-queue/ },
    { to: "/admin/live",         label: "Live",     icon: Radio,           match: /^\/admin\/live/ },
    { to: "/admin/sms",          label: "SMS",      icon: MessageSquare,   match: /^\/admin\/sms/ },
    { moreMenu: true,            label: "More",     icon: MoreHorizontal },
  ];

  // Full drawer menu with every sidebar item — kept in sync with AppShell links
  const drawerItems = role === "client" ? [
    { to: "/portal", label: "My Progress", icon: LayoutDashboard },
    { to: "/portal/services", label: "Start New Services", icon: PlusCircle },
    { to: "/portal/tradelines", label: "Buy Tradelines", icon: Landmark },
    { to: "/portal/messages", label: "Messages", icon: MessageCircle },
    { to: "/portal/meetings", label: "Meetings", icon: CalendarDays },
    { to: "/portal/funding", label: "My Funding Center", icon: Rocket },
    { to: "/portal/referrals", label: "Refer & Earn", icon: Gift },
    { to: "/portal/feed", label: "Money News Today", icon: Sparkles },
    { to: "/profile", label: "My Profile", icon: UserCog },
  ] : isTradelineAdmin ? [
    { to: "/tradeline-admin", label: "Tradeline Ops", icon: Landmark },
    { to: "/profile", label: "My Profile", icon: UserCog },
  ] : ADMIN_ROLES.includes(role) ? [
    { to: "/admin", label: "Clients", icon: LayoutDashboard },
    { to: "/admin/work-queue", label: "Work Queue", icon: ClipboardList },
    { to: "/admin/live", label: "Live Now", icon: Radio },
    { to: "/tradeline-admin", label: "Tradelines", icon: Landmark },
    { to: "/admin/sms", label: "SMS Inbox", icon: MessageSquare },
    { to: "/admin/bulk-schedule", label: "Bulk Booking", icon: CalendarRange },
    { to: "/admin/meetings", label: "Meetings", icon: CalendarDays },
    { to: "/admin/users", label: "Team & Users", icon: Users },
    { to: "/admin/feed", label: "Money News Today", icon: Sparkles },
    { to: "/admin/service-requests", label: "Service Requests", icon: ClipboardList },
    { to: "/admin/goal-insights", label: "Goal Insights", icon: Target },
    { to: "/admin/settings", label: "Automations", icon: Settings },
    { to: "/admin/ticker", label: "Ticker Tape", icon: TrendingUp },
    { to: "/profile", label: "My Profile", icon: UserCog },
  ] : [
    // credit_specialist / team_member
    { to: "/admin", label: "Clients", icon: LayoutDashboard },
    { to: "/admin/work-queue", label: "Work Queue", icon: ClipboardList },
    { to: "/admin/live", label: "Live Now", icon: Radio },
    { to: "/admin/sms", label: "SMS Inbox", icon: MessageSquare },
    { to: "/admin/bulk-schedule", label: "Bulk Booking", icon: CalendarRange },
    { to: "/admin/meetings", label: "Meetings", icon: CalendarDays },
    { to: "/admin/service-requests", label: "Service Requests", icon: ClipboardList },
    { to: "/portal/referrals", label: "Refer & Earn", icon: Gift },
    { to: "/admin/feed", label: "Money News Today", icon: Sparkles },
    { to: "/profile", label: "My Profile", icon: UserCog },
  ];

  // Only "own" the active state for the primary Home item on /portal, and route items on their routes.
  // Progress and Messages are hash-like scroll actions and never take the active state.
  const isActive = (item) => {
    if (item.moreMenu) return drawerOpen;
    if (item.scrollTo && item.scrollTo !== "top") return false;
    return item.match?.test(loc.pathname);
  };

  const handleClick = (e, item) => {
    if (item.moreMenu) {
      e.preventDefault();
      setDrawerOpen(true);
      return;
    }
    // Route-only items use Link default behavior
    if (!item.scrollTo) return;

    e.preventDefault();

    const doScroll = () => {
      if (item.scrollTo === "top") {
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      const el = document.getElementById(item.scrollTo);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    if (loc.pathname !== item.to) {
      nav(item.to);
      setTimeout(doScroll, 120);
    } else {
      doScroll();
    }
  };

  return (
    <>
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-4px_20px_rgba(15,23,42,0.06)]"
      data-testid="mobile-bottom-nav"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className={`grid`} style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
        {items.map((it, i) => {
          const active = isActive(it);
          const Icon = it.icon;
          const isMore = !!it.moreMenu;
          const Comp = isMore ? "button" : Link;
          const compProps = isMore ? { type: "button" } : { to: it.to };
          return (
            <Comp
              key={i}
              {...compProps}
              onClick={(e) => handleClick(e, it)}
              className={`relative flex flex-col items-center justify-center gap-1 py-2.5 px-1 min-h-[60px] text-[10px] font-bold uppercase tracking-wider transition ${
                active
                  ? isMore
                    ? "text-amber-600"
                    : "text-amber-600"
                  : "text-slate-500 hover:text-slate-800 active:bg-slate-100"
              }`}
              data-testid={`mobile-nav-${it.label.toLowerCase()}`}
            >
              {isMore ? (
                <span className="relative">
                  <Icon size={22} strokeWidth={active ? 2.4 : 2} />
                  <span className="absolute -top-1 -right-2 h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                </span>
              ) : (
                <Icon size={22} strokeWidth={active ? 2.4 : 2} />
              )}
              <span>{it.label}</span>
              {active && <span className="absolute top-0 h-[3px] w-8 rounded-b-full bg-amber-500" />}
            </Comp>
          );
        })}
      </div>
    </nav>

    <MobileMoreDrawer
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      items={drawerItems}
      role={role}
    />
    </>
  );
}

function MobileMoreDrawer({ open, onClose, items, role }) {
  const nav = useNavigate();
  if (!open) return null;

  const launchWalkthrough = () => {
    onClose();
    setTimeout(() => window.dispatchEvent(new CustomEvent("cq:walkthrough:start")), 100);
  };

  return (
    <div className="md:hidden fixed inset-0 z-[70] flex flex-col justify-end" data-testid="mobile-more-drawer">
      {/* Backdrop */}
      <button
        aria-label="Close menu"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm animate-[cq-fade-in_0.25s_ease-out]"
      />

      {/* Sheet */}
      <div
        className="relative bg-white rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)", animation: "cq-drawer-up 0.35s cubic-bezier(0.16,1,0.3,1)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grab handle */}
        <div className="pt-3 pb-1 flex justify-center">
          <span className="h-1.5 w-12 rounded-full bg-slate-200" />
        </div>

        {/* Header */}
        <div className="px-5 pt-3 pb-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.28em] text-amber-600">Everywhere in Credit Quack</p>
            <h3 className="font-display text-2xl font-black text-slate-900 mt-0.5">Explore all features</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-500" data-testid="mobile-more-close">
            <X size={20} />
          </button>
        </div>

        {/* Feature grid */}
        <div className="px-4 grid grid-cols-2 gap-2">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <button
                key={it.to}
                onClick={() => { onClose(); nav(it.to); }}
                className="group rounded-2xl bg-slate-50 hover:bg-amber-50 border-2 border-slate-100 hover:border-amber-300 p-4 text-left transition-all"
                style={{ animation: `cq-item-in 0.35s ease-out ${0.05 + i * 0.05}s both` }}
                data-testid={`mobile-more-item-${it.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                <div className="h-10 w-10 rounded-xl bg-white group-hover:bg-amber-500 text-amber-600 group-hover:text-white flex items-center justify-center shadow-sm transition-all">
                  <Icon size={20} />
                </div>
                <p className="mt-2 text-sm font-black text-slate-900 leading-tight">{it.label}</p>
              </button>
            );
          })}
        </div>

        {/* Special CTAs */}
        <div className="mt-4 px-4 space-y-2">
          {role === "client" && (
            <button
              onClick={launchWalkthrough}
              className="w-full flex items-center gap-3 rounded-2xl p-4 text-white bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 shadow-lg font-black text-sm"
              style={{ backgroundSize: "200% 100%", animation: "cq-shimmer-bg 3s linear infinite" }}
              data-testid="mobile-more-walkthrough"
            >
              <Compass size={20} /> Interactive Walkthrough
            </button>
          )}
          <a
            href="https://www.CreditQuack.com"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-3 rounded-2xl p-4 border-2 border-slate-200 hover:border-slate-400 text-slate-700 font-bold text-sm bg-white"
            data-testid="mobile-more-website"
          >
            <ExternalLink size={18} /> Credit Quack Website
          </a>
        </div>
      </div>

      <style>{`
        @keyframes cq-drawer-up { from{transform:translateY(100%);opacity:.4} to{transform:translateY(0);opacity:1} }
        @keyframes cq-fade-in { from{opacity:0} to{opacity:1} }
        @keyframes cq-item-in { from{opacity:0;transform:translateY(14px) scale(0.96)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes cq-shimmer-bg { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
      `}</style>
    </div>
  );
}
